import axios from "axios";
import { isOfflineWorkMode } from "../constants/workMode";
import { emitAuthExpired, getStoredAuth } from "../utils/storage";

const AUTH_TIMEOUT_MS = 90000;
const DEFAULT_TIMEOUT_MS = 60000;
const RETRYABLE_NETWORK_CODES = new Set(["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"]);

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "/api",
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    "Content-Type": "application/json",
  },
});

function isPublicAuthRequest(config) {
  const url = String(config?.url || "");
  return /\/(ssdi-login|repo-agent-login|repo-admin\/login|auth\/login|bank\/login|agent-register|bootstrap-ssdi-admin|health)/.test(
    url
  );
}

function shouldRetryNetworkError(config, error) {
  const isAuth = isPublicAuthRequest(config);
  const maxAttempts = isAuth ? 5 : 2;
  if (!config || config.__networkRetryCount >= maxAttempts) return false;
  if (isAuth && error?.response?.status === 503) return true;
  if (error?.response) return false;
  const code = error?.code || "";
  const message = String(error?.message || "");
  const isNetwork =
    RETRYABLE_NETWORK_CODES.has(code) ||
    message === "Network Error" ||
    /proxy/i.test(message);
  return isNetwork && (isAuth || config.method === "get");
}

function retryDelayMs(attempt, isAuth) {
  if (isAuth) return 4000 * attempt;
  return 3000 + attempt * 2000;
}

api.interceptors.request.use(
  (config) => {
    const savedAuth = getStoredAuth();

    if (savedAuth?.token) {
      config.headers.Authorization = `Bearer ${savedAuth.token}`;
    }

    if (isPublicAuthRequest(config) && !config.timeout) {
      config.timeout = AUTH_TIMEOUT_MS;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;
    const status = error?.response?.status;
    const hadToken = Boolean(getStoredAuth()?.token);

    if (shouldRetryNetworkError(config, error)) {
      config.__networkRetryCount = (config.__networkRetryCount || 0) + 1;
      const isAuth = isPublicAuthRequest(config);
      await new Promise((resolve) =>
        setTimeout(resolve, retryDelayMs(config.__networkRetryCount, isAuth))
      );
      return api(config);
    }

    const paymentMsg = String(error?.response?.data?.message || "");
    const isGatewayAuthError = /razorpay/i.test(paymentMsg);

    const keepOfflineSession =
      isOfflineWorkMode() ||
      (typeof navigator !== "undefined" && navigator.onLine === false);

    if (
      status === 401 &&
      hadToken &&
      !keepOfflineSession &&
      !isPublicAuthRequest(config) &&
      !config?.skipAuthExpire &&
      !isGatewayAuthError
    ) {
      emitAuthExpired();
    }

    return Promise.reject(error);
  }
);

/** Wake Render free-tier instance before login (no-op if already warm). */
export async function wakeApiHealth() {
  try {
    await api.get("/health", { timeout: AUTH_TIMEOUT_MS, skipAuthExpire: true });
  } catch (_err) {
    /* ignore — login retry handles cold start */
  }
}

export default api;
